import { CloseOutlined, PictureFilled } from "@ant-design/icons"
import { Button } from "components/Button/Button"
import { ErrorStatus } from "components/ErrorStatus/ErrorStatus"
import { Spinner } from "components/Spinner/Spinner"
import { Tooltip } from "components/Tooltip/Tooltip"
import { Wrap } from "components/Wrap/Wrap"
import { useDisclosure } from "hooks/useDisclosure"
import { useElement } from "hooks/useElement"
import { useModalNavigate } from "hooks/useModalNavigate"
import { Device, DeviceStatusPeriod } from "interface/device"
import { capitalize } from "lodash-es"
import { ComponentProps, useMemo } from "react"
import { createPortal } from "react-dom"
import { useHotkeys } from "react-hotkeys-hook"
import { useParams } from "react-router"
import {
	useDeviceStatusHistory,
	usePatientDetails,
	usePatientDevice,
	usePatientScreeningsStats,
} from "services/patients"
import { cn } from "styles/cn"
import { asDate, dateFormatter, isToday, isYesterday, today } from "utils/date"

export function DeviceStatus() {
	const { userId: patientId } = useParams()
	const history = useDisclosure()
	const {
		data: device,
		isFetching,
		isError,
		isSuccess,
		error,
	} = usePatientDevice(patientId, ["status"])

	if (!device && isSuccess) {
		return null
	}

	return (
		<>
			<ErrorStatus show={isError}>
				{error?.message ?? "Unable to determine device status"}
			</ErrorStatus>

			{!isError && (
				<Button
					type="text"
					disabled={!device}
					loading={isFetching}
					onClick={history.toggle}
				>
					<div className="flex items-center gap-2">
						{device && <StatusIndicator deviceStatus={device.status} />}
						Footprint is {device ? device.status.toLowerCase() : ""}
					</div>
				</Button>
			)}

			{history.isOpen && device && (
				<History open={history.isOpen} onClose={history.toggle} />
			)}
		</>
	)
}

function History({ open, onClose }: { open: boolean; onClose: VoidFunction }) {
	const containerElement = useElement("layout-content")
	const modalNavigate = useModalNavigate()
	const { scopeId, userId: patientId } = useParams()
	const { data: patient } = usePatientDetails(patientId)
	const { data: device } = usePatientDevice(patientId, ["status"])
	const { data: history = {}, isFetching } = useDeviceStatusHistory(patientId)
	const { data: { stats: screenings = [] } = {} } = usePatientScreeningsStats({
		patientId,
	})
	const days = useMemo(
		() => Object.keys(history).sort((a, b) => a.localeCompare(b)),
		[history],
	)

	useHotkeys("esc", onClose)

	if (!device || !patient || !containerElement || !open) {
		return null
	}

	return createPortal(
		<>
			<div className="absolute inset-0 bg-black/50" />
			<div
				role="dialog"
				aria-labelledby={device.deviceId}
				className={cn(
					"absolute inset-0 m-auto flex flex-col gap-2 overflow-hidden bg-white p-2 text-sm",
					"@4xl/content:gap-4 @4xl/content:rounded-xl @4xl/content:border @4xl/content:border-grey-20 @4xl/content:p-5",
					"max-h-max min-w-full",
					"@4xl/content:max-w-4xl @4xl/content:min-w-0",
				)}
			>
				<div className="flex justify-between gap-1">
					<h2
						id={device.deviceId}
						className="flex items-center gap-2 font-headings text-lg font-bold @4xl/content:text-xl"
					>
						{`${patient?.fullName}'s Footprint`}
						<StatusIndicator deviceStatus={device.status} />
						{capitalize(device.status)}
						{device.lastHeartbeatAt &&
							` (${today().to(device.lastHeartbeatAt)})`}
					</h2>
					<Button
						type="text"
						icon={<CloseOutlined aria-hidden />}
						aria-label="Close"
						className="text-navy-60"
						onClick={onClose}
					/>
				</div>
				<div className="flex flex-col gap-12 pr-4">
					<ul className="flex gap-6 text-lg" aria-label="Status">
						<li className="flex items-center gap-2" aria-label="Online">
							<div className="size-3 rounded-xs bg-mint-100" aria-hidden />
							Online
						</li>
						<li className="flex items-center gap-2" aria-label="Offline">
							<div
								className="size-3 rounded-xs bg-yellow-orange-60"
								aria-hidden
							/>
							Offline
						</li>
					</ul>

					<Spinner spinning={isFetching}>
						<div className="relative flex flex-col gap-14">
							{/* Render Grid Lines (Background) */}
							<div className="absolute inset-y-0 right-0 left-19">
								{HOUR_TICKS.map(({ hour }) => (
									<div
										key={hour}
										className="absolute top-0 bottom-0 w-px bg-grey-20"
										style={{ left: `${(hour / 24) * 100}%` }}
									/>
								))}
							</div>

							{days.map((dayKey) => {
								const periods = history[dayKey]
								const dayHeading = formatDayLabel(dayKey)
								const currentDay = asDate(dayKey)
								const screening = screenings.find(
									({ day }) => currentDay.get("date") === +day,
								)

								return (
									<article
										key={dayKey}
										aria-labelledby={dayKey}
										className="flex items-center"
									>
										<div id={dayKey} className="w-19 shrink-0 text-navy-60">
											{dayHeading}
										</div>

										<ol
											className="relative h-5 flex-1 bg-transparent"
											aria-label={`Device availability ${dayHeading}`}
										>
											{/* Segments/Periods */}
											{periods.map((period, i) => {
												const left = timeToPercentOfDay(period.from, dayKey)
												const width =
													timeToPercentOfDay(period.to, dayKey) - left
												const isOffline = period.status === "OFFLINE"
												const { durationStr, range } = segmentTextParts(period)
												const tooltipTitle = (
													<>
														<span className="text-navy-60">
															{`${capitalize(period.status)} for `}
														</span>
														{durationStr}
														<span className="text-navy-60"> between </span>
														<span className="whitespace-nowrap">{range}</span>
													</>
												)

												return (
													<Wrap
														key={`${period.from}-${period.to}`}
														condition={isOffline}
														with={(children) => (
															<Tooltip title={tooltipTitle}>{children}</Tooltip>
														)}
													>
														<li
															aria-label={`${capitalize(period.status)} for ${durationStr} between ${range}`}
															className={cn(
																"absolute h-full",
																isOffline
																	? "bg-yellow-orange-60"
																	: "bg-mint-100",
																i === 0 && "rounded-l-lg",
																i === periods.length - 1 && "rounded-r-lg",
															)}
															style={{
																left: `${left}%`,
																width: `${Math.max(width, 0.5)}%`,
															}}
														/>
													</Wrap>
												)
											})}

											{/* Screenings/Captures */}
											{screening &&
												screening.capturedAt.map((capturedAt, index) => (
													<Tooltip
														key={`capturedAt-${capturedAt}`}
														title={
															<>
																<span className="text-navy-60">
																	New screening at
																</span>
																{` ${asDate(capturedAt).format("MMM D, YYYY h:mm a")}`}
															</>
														}
													>
														<Button
															type="primary"
															size="large"
															shape="circle"
															icon={<PictureFilled aria-hidden />}
															className={cn("absolute bottom-6")}
															style={{
																left: `calc(${timeToPercentOfDay(capturedAt)}% - 20px)`,
															}}
															aria-label={`Go to data point at ${dateFormatter(capturedAt, { hasYear: false })}`}
															onClick={() =>
																modalNavigate(
																	`/${scopeId}/patients/${patientId}/m/screenings/${screening.ids[index]}`,
																)
															}
														/>
													</Tooltip>
												))}
										</ol>
									</article>
								)
							})}
						</div>
					</Spinner>
				</div>
				<div className="flex">
					<div className="min-w-17" />
					<ol
						className="flex w-full justify-between text-navy-60"
						aria-label="Hour ticks"
					>
						{HOUR_TICKS.map(({ label, hour }) => (
							<li
								key={`${label}-${hour}`}
								className="min-w-8 text-center"
								aria-label={label}
							>
								{label}
							</li>
						))}
					</ol>
				</div>
			</div>
		</>,
		containerElement,
	)
}

function StatusIndicator({
	deviceStatus = "OFFLINE",
	className,
	...props
}: ComponentProps<"div"> & { deviceStatus: Device["status"] }) {
	return (
		<div
			{...props}
			className={cn(
				"size-1.5 min-w-1.5 rounded-full bg-yellow-orange-60",
				deviceStatus === "ONLINE" && "bg-mint-100",
				className,
			)}
		/>
	)
}

function timeToPercentOfDay(
	dateIso: string | Date,
	referenceDateIso?: string | Date,
) {
	const date = asDate(dateIso)

	if (referenceDateIso) {
		const refDate = asDate(referenceDateIso).startOf("day")
		// If date is after refDate, and it represents exactly midnight of the next day, it is 100% of the reference day
		if (
			date.isAfter(refDate, "day") &&
			date.hour() === 0 &&
			date.minute() === 0 &&
			date.second() === 0
		) {
			return 100
		}
	}

	const minutes = date.hour() * 60 + date.minute() + date.second() / 60
	return (minutes / (24 * 60)) * 100
}

function formatDayLabel(dateStr: string) {
	if (isToday(dateStr)) {
		return "Today"
	}
	if (isYesterday(dateStr)) {
		return "Yesterday"
	}
	return asDate(dateStr).format("ddd, MMM D")
}

export function segmentTextParts(period: DeviceStatusPeriod) {
	const start = asDate(period.from)
	const end = asDate(period.to)

	let diffMinutes = 0
	if (
		end.date() !== start.date() ||
		end.month() !== start.month() ||
		end.year() !== start.year()
	) {
		// Period spans to the next day, likely ending exactly at midnight
		diffMinutes = 24 * 60 - (start.hour() * 60 + start.minute())
	} else {
		diffMinutes = Math.abs(
			end.hour() * 60 + end.minute() - (start.hour() * 60 + start.minute()),
		)
	}

	const hours = Math.floor(diffMinutes / 60)
	const minutes = diffMinutes % 60
	const durationStr = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`
	const range = `${start.format("h:mma")} - ${end.format("h:mma")}`

	return { durationStr, range }
}

const HOUR_TICKS = [
	{
		label: "12am",
		hour: 0,
	},
	{
		label: "4am",
		hour: 4,
	},
	{
		label: "8am",
		hour: 8,
	},
	{
		label: "12pm",
		hour: 12,
	},
	{
		label: "4pm",
		hour: 16,
	},
	{
		label: "8pm",
		hour: 20,
	},
	{
		label: "12am",
		hour: 24,
	},
]
